import Dialog from "@components/Dialog"
import { Button, Grid, Typography } from "@mui/material"
import { useGetProductsQuery } from "@store/services/productsService"
import { useAtom } from "jotai"
import { useMemo } from "react"
import { OrgToDivToProds } from "@components/NodeLink/types"
import { emptyLink, linkAtom, linkDialogNodeAtom } from "@components/NodeLink/linkAtom"
import OrgAutocomplete from "@components/NodeLink/OrgAutocomplete"
import DivAutocomplete from "@components/NodeLink/DivAutocomplete"
import ProdAutocomplete from "@components/NodeLink/ProdAutocomplete"
import FamiliesAutocomplete from "@components/NodeLink/FamiliesAutocomplete"
import { useNodeLinkValidation } from "@components/NodeLink/useNodeLinkValidation"
import { linkEncode } from "@components/NodeLink/linkHelpers"
import { AppNode } from "@features/builder/graph/types"
import VersionAutocomplete from "@components/NodeLink/VersionAutocomplete"
import NodeAutocomplete from "@components/NodeLink/NodeAutocomplete"

type ActionsProps = {
  isSaveDisabled: boolean
  onSave: () => void
  onClose: () => void
}

const Actions = (props: ActionsProps) => {
  return (
    <>
      <Button variant="outlined" onClick={props.onClose}>
        Cancel
      </Button>
      <Button variant="contained" disabled={props.isSaveDisabled} onClick={props.onSave}>
        OK
      </Button>
    </>
  )
}

type NodeLinkDialogProps = {
  onSave?: (link: string, node: AppNode | undefined) => void
}

const NodeLinkDialog = (props: NodeLinkDialogProps) => {
  const [linkDialogNode, setLinkDialogNode] = useAtom(linkDialogNodeAtom)
  const [link, setLink] = useAtom(linkAtom)
  const { hasError } = useNodeLinkValidation(link, linkDialogNode)

  const { org, div, prod, family, version, node } = link
  const hasFullNodeGraphLink = org && div && prod && family && version && node
  const isSaveDisabled = !hasFullNodeGraphLink || hasError

  const handleClose = () => {
    setLinkDialogNode(undefined)

    // preserve link, reset only "node" in case someone opens it with another node type
    setLink({ ...link, node: "" })
  }

  const handleSave = () => {
    const nodeGraphLink = linkEncode(
      link.org,
      link.div,
      link.prod,
      link.family,
      link.version,
      link.node,
    )
    props.onSave?.(nodeGraphLink, linkDialogNode)

    setLinkDialogNode(undefined)
    setLink(emptyLink)
  }

  const { data } = useGetProductsQuery({ roleHint: "whatif" })

  const orgToDivToProds: OrgToDivToProds = useMemo(() => {
    const map = new Map<string, Map<string, Set<string>>>()

    for (const product of data?.items ?? []) {
      const org = product.organisation_key
      const div = product.division_key
      const prod = product.key

      if (!map.has(org)) {
        map.set(org, new Map())
      }

      const divToProds = map.get(org)!
      if (!divToProds.has(div)) {
        divToProds.set(div, new Set())
      }

      const prods = divToProds.get(div)!
      prods.add(prod)
    }

    return map
  }, [data?.items])

  return (
    <Dialog
      title="Edit link"
      open={Boolean(linkDialogNode)}
      onClose={handleClose}
      fullWidth
      actionsComponent={
        <Actions isSaveDisabled={isSaveDisabled} onSave={handleSave} onClose={handleClose} />
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Linking connects this node to a node from another graph version. The selected linked node
        will be used as the source for this node in the current scenario.
      </Typography>

      {
        <Grid gap={2} container direction="column">
          <Grid item>
            <OrgAutocomplete orgToDivToProds={orgToDivToProds} />
            <Typography variant="caption" color="text.secondary">
              Choose the organization that owns the graph you want to link from.
            </Typography>
          </Grid>
          <Grid item>
            <DivAutocomplete orgToDivToProds={orgToDivToProds} />
            <Typography variant="caption" color="text.secondary">
              Select division within the chosen organization.
            </Typography>
          </Grid>
          <Grid item>
            <ProdAutocomplete orgToDivToProds={orgToDivToProds} />
            <Typography variant="caption" color="text.secondary">
              Select product within the chosen organization and division.
            </Typography>
          </Grid>
          <Grid item>
            <FamiliesAutocomplete />
            <Typography variant="caption" color="text.secondary">
              Pick a product family you want to use as the link target.
            </Typography>
          </Grid>
          <Grid item>
            <VersionAutocomplete />
            <Typography variant="caption" color="text.secondary">
              Choose a family version to link to.
            </Typography>
          </Grid>
          <Grid item>
            <NodeAutocomplete />
            <Typography variant="caption" color="text.secondary">
              Select the exact node from the selected graph that this node should be linked to.
            </Typography>
          </Grid>
        </Grid>
      }
    </Dialog>
  )
}

export default NodeLinkDialog
